import { useMemo, useState, useEffect } from "react";
import ThemeSwitcher from "../components/ThemeSwitcher";
import { getDashboard } from "../store/slices/auth";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

export const bodyClass =
    "bg-slate-950 text-slate-100 min-h-screen flex flex-col font-sans selection:bg-blue-600 selection:text-white";

const getId = (value) =>
    typeof value === "object" ? value?._id || "" : value || "";

const getOfficerName = (officer) =>
    officer?.displayName ||
    officer?.name ||
    officer?.username ||
    "Unknown Officer";

const getOfficerCode = (officer) =>
    officer?.officerId || officer?.employeeId || "--";

const getBoothName = (booth) =>
    booth?.name || booth?.boothName || "Unknown Booth";

const getSelectedOfficer = (id, officers) =>
    officers.find(
        (officer) => String(officer._id) === String(getId(id))
    ) || (typeof id === "object" ? id : null);

const getSelectedBooth = (id, booths) =>
    booths.find(
        (booth) => String(booth._id) === String(getId(id))
    ) || (typeof id === "object" ? id : null);

const isDSP = (officer) =>
    String(officer?.designation || "").toUpperCase().includes("DSP");

export default function StrikingForcesMarkup({
    strikingForces = [],
    totalStrikingForces = 0,
    statistics = {},

    loading = false,
    submitting = false,
    detailsLoading = false,
    error = null,

    search,
    setSearch,
    status,
    setStatus,

    viewMode,
    setViewMode,

    showModal,
    editingStrikingForceId,
    form,
    formError,

    officers = [],
    booths = [],

    onAddStrikingForce,
    onEditStrikingForce,
    onCloseModal,
    onFormChange,
    onUserToggle,
    onBoothToggle,
    onSubmit,
}) {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { user, dashboard, dashboardLoading } = useSelector(
        (state) => state.auth
    );

    useEffect(() => {
        dispatch(getDashboard());
    }, [dispatch]);

    const [officerSearch, setOfficerSearch] = useState("");
    const [boothSearch, setBoothSearch] = useState("");

    const dspOfficers = useMemo(
        () => officers.filter(isDSP),
        [officers]
    );

    const selectedUsers = useMemo(
        () =>
            (form.users || [])
                .map((id) => getSelectedOfficer(id, officers))
                .filter(Boolean),
        [form.users, officers]
    );

    const selectedBooths = useMemo(
        () =>
            (form.boothIds || [])
                .map((id) => getSelectedBooth(id, booths))
                .filter(Boolean),
        [form.boothIds, booths]
    );

    const selectedDSP = getSelectedOfficer(form.dspId, officers);

    const availableUsers = officers.filter(
        (officer) => String(officer._id) !== String(form.dspId)
    );

    const filteredOfficers = availableUsers.filter((officer) => {
        const text = officerSearch.trim().toLowerCase();

        return (
            !text ||
            [
                getOfficerName(officer),
                getOfficerCode(officer),
                officer.designation,
            ].some((value) =>
                String(value || "").toLowerCase().includes(text)
            )
        );
    });

    const filteredBooths = booths.filter((booth) => {
        const text = boothSearch.trim().toLowerCase();

        return (
            !text ||
            [
                booth.name,
                booth.boothId,
                booth.code,
            ].some((value) =>
                String(value || "").toLowerCase().includes(text)
            )
        );
    });

    const isSaving = submitting;

    const getStrikingForceDSP = (item) =>
        typeof item.dspId === "object"
            ? item.dspId
            : getSelectedOfficer(item.dspId, officers);

    const getStrikingForceUsers = (item) =>
        Array.isArray(item.users) ? item.users : [];

    const getStrikingForceBooths = (item) =>
        Array.isArray(item.boothIds) ? item.boothIds : [];

    const handleLogout = () => {
        const confirmed = window.confirm(
            "Are you sure you want to logout?"
        );

        if (!confirmed) return;

        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        localStorage.removeItem("admin");

        sessionStorage.clear();

        if (window.adminSocket) {
            window.adminSocket.disconnect();
            window.adminSocket = null;
        }

        window.location.replace("/login");
    };

    return (
        <>
            {/* HEADER */}
            <header className="bg-slate-900 border-b border-slate-800 px-4 py-2 flex items-center justify-between shrink-0 shadow-md h-16 relative z-30">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-700 via-indigo-600 to-blue-500 flex items-center justify-center text-white shadow-lg text-xl font-black shrink-0 border border-blue-400/30">
                        🇮🇳
                    </div>

                    <div>
                        <h1 className="text-sm font-black tracking-tight text-white uppercase">
                            Nellore Police
                        </h1>
                        <p className="text-[10px] text-slate-400">
                            Striking Force Administration
                        </p>
                    </div>
                </div>

                <nav className="hidden lg:flex items-center bg-slate-950 p-1 rounded-xl border border-slate-800 text-xs font-semibold shadow-inner">
                    <button
                        type="button"
                        onClick={() => navigate("/admin")}
                        className="px-3 py-1.5 text-slate-300 hover:text-white hover:bg-slate-900 rounded-lg transition"
                    >
                        🗺️ Live Map
                    </button>

                    <button
                        type="button"
                        onClick={() => navigate("/booths")}
                        className="px-3 py-1.5 text-slate-300 hover:text-white hover:bg-slate-900 rounded-lg transition"
                    >
                        🗳️ Booths
                    </button>

                    <button
                        type="button"
                        onClick={() => navigate("/officers")}
                        className="px-3 py-1.5 text-slate-300 hover:text-white hover:bg-slate-900 rounded-lg transition"
                    >
                        👮 Officers
                    </button>
                </nav>

                <div className="flex items-center gap-2">
                    <ThemeSwitcher
                        className="mr-0.5"
                        labelClass="hidden 2xl:inline"
                    />

                    <div className="h-6 w-px bg-slate-800 mx-0.5" />

                    <div className="flex items-center gap-2 pl-1">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black shadow border border-yellow-400/40 bg-blue-950 text-yellow-200">
                            🦁
                        </div>

                        <div className="hidden sm:block text-right">
                            <div className="text-xs font-black text-white leading-tight truncate max-w-[130px]">
                                {user?.displayName ||
                                    user?.name ||
                                    user?.username ||
                                    "Admin"}
                            </div>

                            <div className="text-[10px] text-amber-300 font-bold leading-tight truncate max-w-[160px]">
                                👑 {user?.designation || user?.role || "Administrator"}
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={handleLogout}
                            title="Logout"
                            className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-red-900/40 text-slate-300 hover:text-red-300 border border-slate-700 flex items-center justify-center text-sm transition"
                        >
                            🚪
                        </button>
                    </div>
                </div>
            </header>

            {/* KPI STRIP */}
            <div className="bg-slate-900/90 border-b border-slate-800/80 px-4 py-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 text-xs">
                <div className="bg-slate-800/60 rounded-lg p-2 border border-slate-700/60 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] text-blue-400 uppercase font-bold tracking-wider">
                            Sub Divisions
                        </div>
                        <div className="text-base font-black text-blue-300">
                            {dashboardLoading ? "..." : dashboard?.totalSubDivisions ?? 0}
                        </div>
                    </div>
                    <span className="text-xl opacity-50">🏢</span>
                </div>

                <div className="bg-slate-800/60 rounded-lg p-2 border border-slate-700/60 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] text-sky-400 uppercase font-bold tracking-wider">
                            Circles
                        </div>
                        <div className="text-base font-black text-sky-300">
                            {dashboardLoading ? "..." : dashboard?.totalCircles ?? 0}
                        </div>
                    </div>
                    <span className="text-xl opacity-50">⭕</span>
                </div>

                <div className="bg-slate-800/60 rounded-lg p-2 border border-slate-700/60 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] text-teal-400 uppercase font-bold tracking-wider">
                            Police Stations
                        </div>
                        <div className="text-base font-black text-teal-300">
                            {dashboardLoading ? "..." : dashboard?.totalStations ?? 0}
                        </div>
                    </div>
                    <span className="text-xl opacity-50">🚔</span>
                </div>

                <div className="bg-slate-800/60 rounded-lg p-2 border border-slate-700/60 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] text-indigo-400 uppercase font-bold tracking-wider">
                            Total Officers
                        </div>
                        <div className="text-base font-black text-indigo-300">
                            {dashboardLoading ? "..." : dashboard?.totalOfficers ?? 0}
                        </div>
                    </div>
                    <span className="text-xl opacity-50">👮</span>
                </div>

                <div className="bg-slate-800/60 rounded-lg p-2 border border-slate-700/60 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] text-emerald-400 uppercase font-bold tracking-wider">
                            Total Booths
                        </div>
                        <div className="text-base font-black text-emerald-300">
                            {dashboardLoading ? "..." : dashboard?.totalPollingStations ?? 0}
                        </div>
                    </div>
                    <span className="text-xl opacity-50">🗳️</span>
                </div>
            </div>

            {/* PAGE TITLE */}
            <section className="px-5 py-5 border-slate-800 bg-slate-900/50">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-blue-400 font-bold mb-1">
                                District Administration / Special Units
                            </div>

                            <h2 className="text-xl font-black text-white">
                                🚨 Striking Force Management
                            </h2>

                            <p className="text-xs text-slate-400 mt-1">
                                Manage Striking Force units, DSP in-charge, assigned officers and polling booths.
                            </p>
                        </div>

                        <button
                            type="button"
                            onClick={onAddStrikingForce}
                            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-blue-700"
                        >
                            <i className="fa-solid fa-plus" />
                            Add Striking Force
                        </button>
                    </div>
                </div>
            </section>

            {/* STATISTICS */}
            <section className="px-5 py-4 border-slate-800">
                <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-slate-900 border border-slate-800 rounded-xl p-3">
                        <p className="text-[10px] uppercase text-slate-400 font-bold">
                            Total Striking Forces
                        </p>
                        <h3 className="text-xl font-black text-white mt-1">
                            {statistics.total || 0}
                        </h3>
                    </div>

                    <div className="bg-emerald-950/30 border border-emerald-700/40 rounded-xl p-3">
                        <p className="text-[10px] uppercase text-emerald-400 font-bold">
                            Active Units
                        </p>
                        <h3 className="text-xl font-black text-emerald-400 mt-1">
                            {statistics.active || 0}
                        </h3>
                    </div>

                    <div className="bg-indigo-950/30 border border-indigo-700/40 rounded-xl p-3">
                        <p className="text-[10px] uppercase text-indigo-400 font-bold">
                            Assigned Officers
                        </p>
                        <h3 className="text-xl font-black text-indigo-400 mt-1">
                            {statistics.totalUsers || 0}
                        </h3>
                    </div>

                    <div className="bg-sky-950/30 border border-sky-700/40 rounded-xl p-3">
                        <p className="text-[10px] uppercase text-sky-400 font-bold">
                            Assigned Booths
                        </p>
                        <h3 className="text-xl font-black text-sky-400 mt-1">
                            {statistics.totalBooths || 0}
                        </h3>
                    </div>
                </div>
            </section>

            {/* FILTERS */}
            <section className="px-5 py-4 border-slate-800 bg-slate-950">
                <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-3">
                    <div className="relative flex-1">
                        <span className="absolute left-3 top-2.5 text-slate-500">
                            🔍
                        </span>

                        <input
                            type="text"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Search striking force or DSP..."
                            className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-9 pr-3 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                        />
                    </div>

                    <select
                        value={status}
                        onChange={(event) => setStatus(event.target.value)}
                        className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white outline-none"
                    >
                        <option value="ALL">All Statuses</option>
                        <option value="ACTIVE">Active</option>
                        <option value="INACTIVE">Inactive</option>
                    </select>

                    <div className="flex items-center bg-slate-900 border border-slate-700 rounded-xl p-1">
                        <button
                            type="button"
                            onClick={() => setViewMode("grid")}
                            className={`px-3 py-2 rounded-lg text-xs font-bold ${viewMode === "grid"
                                    ? "bg-blue-600 text-white"
                                    : "text-slate-400 hover:text-white"
                                }`}
                        >
                            <i className="fa-solid fa-grip mr-2" />
                            Grid
                        </button>

                        <button
                            type="button"
                            onClick={() => setViewMode("list")}
                            className={`px-3 py-2 rounded-lg text-xs font-bold ${viewMode === "list"
                                    ? "bg-blue-600 text-white"
                                    : "text-slate-400 hover:text-white"
                                }`}
                        >
                            <i className="fa-solid fa-list mr-2" />
                            List
                        </button>
                    </div>

                    <button
                        type="button"
                        onClick={() => {
                            setSearch("");
                            setStatus("ALL");
                        }}
                        className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-xl text-xs font-bold text-slate-300"
                    >
                        Reset Filters
                    </button>
                </div>

                <div className="max-w-7xl mx-auto mt-3 text-[10px] text-slate-500">
                    Showing{" "}
                    <span className="text-white font-bold">
                        {strikingForces.length}
                    </span>{" "}
                    of {totalStrikingForces} Striking Forces
                </div>
            </section>

            {/* STRIKING FORCE CONTENT */}
            <main className="flex-1 p-5 bg-slate-950">
                <div className="max-w-7xl mx-auto">
                    {loading ? (
                        <div className="text-center py-20 text-slate-400 text-sm">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3" />
                            Loading Striking Forces...
                        </div>
                    ) : error ? (
                        <div className="text-center py-16 bg-red-950/20 border border-red-800/40 rounded-2xl">
                            <p className="text-red-400 text-sm font-bold">
                                Failed to load Striking Forces
                            </p>
                            <p className="text-xs text-slate-400 mt-2">
                                {error}
                            </p>
                        </div>
                    ) : strikingForces.length === 0 ? (
                        <div className="text-center py-20 bg-slate-900 border border-slate-800 rounded-2xl">
                            <div className="text-4xl mb-3">🚨</div>
                            <h3 className="text-sm font-bold text-white">
                                No Striking Forces Found
                            </h3>
                            <p className="text-xs text-slate-500 mt-2">
                                Add a Striking Force unit to get started.
                            </p>
                        </div>
                    ) : viewMode === "grid" ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {strikingForces.map((item) => {
                                const dsp = getStrikingForceDSP(item);
                                const users = getStrikingForceUsers(item);
                                const assignedBooths = getStrikingForceBooths(item);

                                return (
                                    <div
                                        key={item._id}
                                        className="bg-slate-900 border border-slate-800 rounded-2xl p-4 hover:border-blue-500/60 transition shadow-lg"
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <h3 className="text-sm font-black text-white">
                                                    {item.name || "Unnamed Striking Force"}
                                                </h3>

                                                <p className="text-[10px] text-slate-500 mt-1">
                                                    Striking Force Unit
                                                </p>
                                            </div>

                                            <span
                                                className={`text-[9px] px-2 py-1 rounded-lg border font-black ${String(item.status || "ACTIVE").toUpperCase() === "ACTIVE"
                                                        ? "bg-emerald-950 text-emerald-400 border-emerald-800"
                                                        : "bg-red-950 text-red-400 border-red-800"
                                                    }`}
                                            >
                                                {item.status || "ACTIVE"}
                                            </span>
                                        </div>

                                        {/* DSP IN-CHARGE */}
                                        <div className="mt-4 p-3 bg-blue-950/30 border border-blue-800/40 rounded-xl">
                                            <p className="text-[9px] uppercase tracking-wider text-blue-400 font-black mb-2">
                                                DSP In-charge
                                            </p>

                                            <p className="text-xs font-bold text-white">
                                                {dsp ? getOfficerName(dsp) : "--"}
                                            </p>

                                            <p className="text-[10px] text-slate-400 mt-1">
                                                {dsp
                                                    ? `${getOfficerCode(dsp)} • ${dsp.designation || "DSP"}`
                                                    : "No DSP assigned"}
                                            </p>

                                            {dsp?.phone && (
                                                <p className="text-[10px] text-slate-400 mt-1">
                                                    📞 {dsp.phone}
                                                </p>
                                            )}
                                        </div>

                                        {/* ASSIGNED USERS */}
                                        <div className="mt-4 pt-3 border-t border-slate-800">
                                            <div className="flex items-center justify-between mb-3">
                                                <h4 className="text-[11px] font-black text-white">
                                                    👮 Assigned Officers
                                                </h4>

                                                <span className="text-[10px] px-2 py-1 rounded-lg bg-indigo-950 text-indigo-400 font-bold">
                                                    {users.length} Officers
                                                </span>
                                            </div>

                                            {users.length === 0 ? (
                                                <p className="text-[10px] text-slate-500 py-2">
                                                    No officers assigned.
                                                </p>
                                            ) : (
                                                <div className="space-y-2">
                                                    {users.map((userItem, index) => {
                                                        const officer =
                                                            typeof userItem === "object"
                                                                ? userItem
                                                                : getSelectedOfficer(userItem, officers);

                                                        return (
                                                            <div
                                                                key={getId(userItem) || index}
                                                                className="bg-slate-950 border border-slate-800 rounded-xl p-3"
                                                            >
                                                                <p className="text-[11px] font-bold text-white">
                                                                    {officer
                                                                        ? getOfficerName(officer)
                                                                        : "Unknown Officer"}
                                                                </p>

                                                                <div className="flex justify-between gap-2 mt-1 text-[9px] text-slate-400">
                                                                    <span>
                                                                        {officer
                                                                            ? getOfficerCode(officer)
                                                                            : "--"}
                                                                    </span>

                                                                    <span>
                                                                        {officer?.designation || "--"}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>

                                        {/* ASSIGNED BOOTHS */}
                                        <div className="mt-4 pt-3 border-t border-slate-800">
                                            <div className="flex items-center justify-between mb-3">
                                                <h4 className="text-[11px] font-black text-white">
                                                    🗳️ Assigned Booths
                                                </h4>

                                                <span className="text-[10px] px-2 py-1 rounded-lg bg-sky-950 text-sky-400 font-bold">
                                                    {assignedBooths.length} Booths
                                                </span>
                                            </div>

                                            {assignedBooths.length === 0 ? (
                                                <p className="text-[10px] text-slate-500 py-2">
                                                    No booths assigned.
                                                </p>
                                            ) : (
                                                <div className="flex flex-wrap gap-2">
                                                    {assignedBooths.map((booth, index) => {
                                                        const boothData =
                                                            typeof booth === "object"
                                                                ? booth
                                                                : getSelectedBooth(booth, booths);

                                                        return (
                                                            <span
                                                                key={getId(booth) || index}
                                                                className="text-[9px] px-2 py-1 rounded-lg bg-slate-800 border border-slate-700 text-slate-300"
                                                            >
                                                                {boothData
                                                                    ? `${getBoothName(boothData)} (${boothData.boothId || boothData.code || "--"})`
                                                                    : "Unknown Booth"}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>

                                        <div className="mt-4 pt-3 border-t border-slate-800 flex justify-end">
                                            <button
                                                type="button"
                                                onClick={() => onEditStrikingForce(item)}
                                                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold"
                                            >
                                                <i className="fa-solid fa-pen-to-square mr-2" />
                                                Edit Striking Force
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        /* LIST VIEW */
                        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-x-auto">
                            <table className="w-full min-w-[900px] text-left">
                                <thead className="bg-slate-950 text-[10px] uppercase text-slate-400">
                                    <tr>
                                        <th className="px-4 py-4">Striking Force</th>
                                        <th className="px-4 py-4">DSP In-charge</th>
                                        <th className="px-4 py-4">Officers</th>
                                        <th className="px-4 py-4">Booths</th>
                                        <th className="px-4 py-4">Status</th>
                                        <th className="px-4 py-4 text-right">Action</th>
                                    </tr>
                                </thead>

                                <tbody className="divide-y divide-slate-800">
                                    {strikingForces.map((item) => {
                                        const dsp = getStrikingForceDSP(item);

                                        return (
                                            <tr
                                                key={item._id}
                                                className="hover:bg-slate-800/50"
                                            >
                                                <td className="px-4 py-4">
                                                    <p className="text-xs font-bold text-white">
                                                        {item.name}
                                                    </p>
                                                </td>

                                                <td className="px-4 py-4 text-xs text-slate-300">
                                                    {dsp ? getOfficerName(dsp) : "--"}
                                                    <p className="text-[9px] text-slate-500 mt-1">
                                                        {dsp ? getOfficerCode(dsp) : ""}
                                                    </p>
                                                </td>

                                                <td className="px-4 py-4 text-xs text-slate-300">
                                                    {getStrikingForceUsers(item).length}
                                                </td>

                                                <td className="px-4 py-4 text-xs text-slate-300">
                                                    {getStrikingForceBooths(item).length}
                                                </td>

                                                <td className="px-4 py-4">
                                                    <span
                                                        className={`text-[9px] px-2 py-1 rounded-lg border font-bold ${String(item.status || "ACTIVE").toUpperCase() === "ACTIVE"
                                                                ? "bg-emerald-950 text-emerald-400 border-emerald-800"
                                                                : "bg-red-950 text-red-400 border-red-800"
                                                            }`}
                                                    >
                                                        {item.status || "ACTIVE"}
                                                    </span>
                                                </td>

                                                <td className="px-4 py-4 text-right">
                                                    <button
                                                        type="button"
                                                        onClick={() => onEditStrikingForce(item)}
                                                        className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-bold"
                                                    >
                                                        <i className="fa-solid fa-pen-to-square mr-1" />
                                                        Edit
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </main>

            {/* FOOTER */}
            <footer className="px-5 py-3 border-t border-slate-800 bg-slate-900 text-center">
                <p className="text-[10px] text-slate-500">
                    Nellore Police • Striking Force Administration
                </p>
            </footer>

            {/* ADD / EDIT MODAL */}
            {showModal && (
                <div className="fixed inset-0 z-[100] bg-black/75 backdrop-blur-sm flex items-center justify-center p-3 sm:p-5">
                    <div className="w-full max-w-4xl max-h-[95vh] overflow-y-auto bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl">
                        {/* MODAL HEADER */}
                        <div className="sticky top-0 z-10 bg-slate-900 border-b border-slate-800 px-5 py-4 flex items-center justify-between">
                            <div>
                                <h3 className="text-base font-black text-white">
                                    {editingStrikingForceId
                                        ? "Edit Striking Force"
                                        : "Add Striking Force"}
                                </h3>

                                <p className="text-[10px] text-slate-400 mt-1">
                                    Enter unit details and assign DSP, officers and polling booths.
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={onCloseModal}
                                disabled={isSaving}
                                className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 disabled:opacity-40"
                            >
                                ✕
                            </button>
                        </div>

                        {detailsLoading ? (
                            <div className="py-20 text-center text-sm text-slate-400">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3" />
                                Loading Striking Force details...
                            </div>
                        ) : (
                            <form onSubmit={onSubmit} className="p-5 space-y-6">
                                {formError && (
                                    <div className="bg-red-950/40 border border-red-800 rounded-xl p-3 text-xs text-red-300">
                                        {formError}
                                    </div>
                                )}

                                {/* BASIC DETAILS */}
                                <section>
                                    <h4 className="text-xs font-black uppercase tracking-wider text-blue-400 mb-3">
                                        Striking Force Details
                                    </h4>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="md:col-span-2">
                                            <label className="block text-[10px] text-slate-400 mb-1">
                                                Striking Force Name *
                                            </label>

                                            <input
                                                required
                                                value={form.name}
                                                onChange={(event) =>
                                                    onFormChange("name", event.target.value)
                                                }
                                                placeholder="Striking Force 1"
                                                className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                                            />
                                        </div>
                                    </div>
                                </section>

                                {/* DSP IN-CHARGE */}
                                <section>
                                    <h4 className="text-xs font-black uppercase tracking-wider text-blue-400 mb-3">
                                        DSP In-charge
                                    </h4>

                                    <div>
                                        <label className="block text-[10px] text-slate-400 mb-1">
                                            Select DSP *
                                        </label>

                                        <select
                                            required
                                            value={form.dspId}
                                            onChange={(event) =>
                                                onFormChange("dspId", event.target.value)
                                            }
                                            className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                                        >
                                            <option value="">
                                                Select DSP In-charge
                                            </option>

                                            {dspOfficers.map((officer) => (
                                                <option
                                                    key={officer._id}
                                                    value={officer._id}
                                                >
                                                    {getOfficerName(officer)} — {getOfficerCode(officer)} ({officer.designation})
                                                </option>
                                            ))}
                                        </select>

                                        {selectedDSP && (
                                            <div className="mt-2 p-3 bg-blue-950/30 border border-blue-800/40 rounded-xl">
                                                <p className="text-xs font-bold text-white">
                                                    {getOfficerName(selectedDSP)}
                                                </p>
                                                <p className="text-[10px] text-slate-400 mt-1">
                                                    {getOfficerCode(selectedDSP)} • {selectedDSP.designation || "DSP"}
                                                </p>
                                            </div>
                                        )}

                                        {dspOfficers.length === 0 && (
                                            <p className="text-[10px] text-amber-400 mt-2">
                                                No officers with DSP designation were found.
                                            </p>
                                        )}
                                    </div>
                                </section>

                                {/* USERS */}
                                <section>
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                                        <h4 className="text-xs font-black uppercase tracking-wider text-blue-400">
                                            Assigned Officers
                                        </h4>

                                        <span className="text-[10px] px-2 py-1 rounded-lg bg-indigo-950 text-indigo-400 font-bold">
                                            {form.users.length} Selected
                                        </span>
                                    </div>

                                    <input
                                        type="text"
                                        value={officerSearch}
                                        onChange={(event) =>
                                            setOfficerSearch(event.target.value)
                                        }
                                        placeholder="Search officer by name, ID or designation..."
                                        className="w-full mb-3 bg-slate-950 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                                    />

                                    <div className="max-h-64 overflow-y-auto border border-slate-800 rounded-xl divide-y divide-slate-800">
                                        {filteredOfficers.length === 0 ? (
                                            <p className="p-4 text-center text-xs text-slate-500">
                                                No officers found.
                                            </p>
                                        ) : (
                                            filteredOfficers.map((officer) => {
                                                const checked = form.users.some(
                                                    (id) =>
                                                        String(id) === String(officer._id)
                                                );

                                                return (
                                                    <label
                                                        key={officer._id}
                                                        className="flex items-center gap-3 p-3 hover:bg-slate-800/60 cursor-pointer"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={(event) =>
                                                                onUserToggle(
                                                                    officer._id,
                                                                    event.target.checked
                                                                )
                                                            }
                                                            className="w-4 h-4 accent-blue-600"
                                                        />

                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs font-bold text-white">
                                                                {getOfficerName(officer)}
                                                            </p>

                                                            <p className="text-[10px] text-slate-500 mt-1">
                                                                {getOfficerCode(officer)} • {officer.designation || "Officer"}
                                                            </p>
                                                        </div>

                                                        {checked && (
                                                            <span className="text-[9px] text-emerald-400 font-bold">
                                                                SELECTED
                                                            </span>
                                                        )}
                                                    </label>
                                                );
                                            })
                                        )}
                                    </div>

                                    {selectedUsers.length > 0 && (
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            {selectedUsers.map((officer) => (
                                                <span
                                                    key={officer._id}
                                                    className="text-[10px] px-2 py-1 rounded-lg bg-indigo-950 border border-indigo-800 text-indigo-300"
                                                >
                                                    {getOfficerName(officer)}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </section>

                                {/* BOOTHS */}
                                <section>
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                                        <h4 className="text-xs font-black uppercase tracking-wider text-blue-400">
                                            Assigned Polling Booths
                                        </h4>

                                        <span className="text-[10px] px-2 py-1 rounded-lg bg-sky-950 text-sky-400 font-bold">
                                            {form.boothIds.length} Selected
                                        </span>
                                    </div>

                                    <input
                                        type="text"
                                        value={boothSearch}
                                        onChange={(event) =>
                                            setBoothSearch(event.target.value)
                                        }
                                        placeholder="Search booth by name or booth ID..."
                                        className="w-full mb-3 bg-slate-950 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                                    />

                                    <div className="max-h-64 overflow-y-auto border border-slate-800 rounded-xl divide-y divide-slate-800">
                                        {filteredBooths.length === 0 ? (
                                            <p className="p-4 text-center text-xs text-slate-500">
                                                No polling booths found.
                                            </p>
                                        ) : (
                                            filteredBooths.map((booth) => {
                                                const checked = form.boothIds.some(
                                                    (id) =>
                                                        String(id) === String(booth._id)
                                                );

                                                return (
                                                    <label
                                                        key={booth._id}
                                                        className="flex items-center gap-3 p-3 hover:bg-slate-800/60 cursor-pointer"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={(event) =>
                                                                onBoothToggle(
                                                                    booth._id,
                                                                    event.target.checked
                                                                )
                                                            }
                                                            className="w-4 h-4 accent-blue-600"
                                                        />

                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs font-bold text-white">
                                                                {getBoothName(booth)}
                                                            </p>

                                                            <p className="text-[10px] text-slate-500 mt-1">
                                                                {booth.boothId || booth.code || "--"}
                                                                {booth.policeStationName
                                                                    ? ` • ${booth.policeStationName}`
                                                                    : ""}
                                                            </p>
                                                        </div>

                                                        {checked && (
                                                            <span className="text-[9px] text-emerald-400 font-bold">
                                                                SELECTED
                                                            </span>
                                                        )}
                                                    </label>
                                                );
                                            })
                                        )}
                                    </div>

                                    {selectedBooths.length > 0 && (
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            {selectedBooths.map((booth) => (
                                                <span
                                                    key={booth._id}
                                                    className="text-[10px] px-2 py-1 rounded-lg bg-sky-950 border border-sky-800 text-sky-300"
                                                >
                                                    {getBoothName(booth)}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </section>

                                {/* FORM ACTIONS */}
                                <div className="pt-4 border-t border-slate-800 flex flex-col-reverse sm:flex-row justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={onCloseModal}
                                        disabled={isSaving}
                                        className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold disabled:opacity-40"
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="submit"
                                        disabled={isSaving}
                                        className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold disabled:opacity-50"
                                    >
                                        {isSaving ? (
                                            <>
                                                <i className="fa-solid fa-spinner fa-spin mr-2" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa-solid fa-floppy-disk mr-2" />
                                                {editingStrikingForceId
                                                    ? "Update Striking Force"
                                                    : "Create Striking Force"}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}